import { Component, createRef } from 'react';
import styles from './calc-result-bar.module.scss';

interface Props {
    label?: string;
}

interface State {
    value: string;
    label: string;
}

// Mobile-only sticky bar pinned to the top of the calculator card. Reads
// the headline from the nearest `.resultBox > .resultValue` (and label
// from `.resultLabel`) on each parent render so users always see the
// computed result even while scrolling a long input form. Tap the
// chevron to smooth-scroll to the full result panel.
//
// Hidden via CSS at >768px where the form/result split already keeps
// both visible.
export class CalcResultBar extends Component<Props, State> {
    state: State = { value: '', label: '' };
    private observer: MutationObserver | null = null;
    private ref = createRef<HTMLDivElement>();

    componentDidMount() {
        this.refresh();
        const parent = this.ref.current?.parentElement;
        if (!parent) return;
        // Watch the parent subtree for DOM changes so we re-read the value
        // whenever inputs change without each calculator wiring a callback.
        this.observer = new MutationObserver(() => this.refresh());
        this.observer.observe(parent, { childList: true, subtree: true, characterData: true });
    }

    componentWillUnmount() {
        this.observer?.disconnect();
    }

    private refresh = () => {
        const parent = this.ref.current?.parentElement;
        if (!parent) return;
        const box = parent.querySelector('[class*="resultBox"]');
        if (!box) return;
        const val = box.querySelector('[class*="resultValue"]')?.textContent?.trim() || '';
        const lblEl = box.querySelector('[class*="resultLabel"]')?.textContent?.trim();
        const lbl = lblEl || this.props.label || 'Result';
        if (val !== this.state.value || lbl !== this.state.label) {
            this.setState({ value: val, label: lbl });
        }
    };

    private scrollToResult = () => {
        const parent = this.ref.current?.parentElement;
        const box = parent?.querySelector('[class*="resultBox"]') as HTMLElement | null;
        box?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    render() {
        const { value, label } = this.state;
        if (!value) {
            return <div ref={this.ref} aria-hidden style={{ display: 'none' }} />;
        }
        // Strip trailing parens/units from label for tighter mobile display.
        const shortLabel = label.split(/[(:]/)[0].trim();
        return (
            <div ref={this.ref} className={styles.bar} role="status" aria-live="polite">
                <span className={styles.label}>{shortLabel}</span>
                <span className={styles.value} title={value}>{value}</span>
                <button type="button" className={styles.jumpBtn} onClick={this.scrollToResult}
                    aria-label="Jump to full result">↓</button>
            </div>
        );
    }
}

export default CalcResultBar;
